import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { MAP_ATTRIBUTION_GEOCOMM } from '@rsos/capstone/src/constants/capabilities';
import { withLayout } from '@rsos/components/capstone/base/Layout';
import { useZoomAndLocationContext } from '@rsos/components/capstone/common/Map/ZoomAndLocationContext';
import { EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { useFlag } from '@rsos/flags';
import { initializeMap } from '@rsos/maps';
import { ESRI } from '@rsos/maps/constants/mapProviders';
import { normalizeExtent } from '@rsos/maps/utils/utils';
import { filteredAlertsSelector } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import { DARK_THEME } from '@rsos/theme/constants';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import checkGISServicesAccess from '@rsos/utils/checkGISServicesAccess';
import exportToGlobalNamespace from '@rsos/utils/exportToGlobalNamespace';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import isValidCoordinates from '@rsos/utils/isValidCoordinates';
import { useIsMapIconDisabled } from '@rsos/utils/useIsMapIconDisabled';
import usePortalType from '@rsos/utils/usePortalType';
import useEsriMapTransition from '../../../hooks/useEsriMapTransition';
import useGISBasemapLayer from '../../../hooks/useGISBasemapLayer';
import { useGISLayersOpacity } from '../../../hooks/useGISLayersOpacity';
import useGISServiceLayers from '../../../hooks/useGISServiceLayers';
import useReorderEsriMapMarkers from '../../../hooks/useReorderEsriMapMarkers';
import { RecenterMapButton } from '../../Map/RecenterMapButton';
import DeckGLNext from '../../Map/useDeckGL/DeckGLNext';
import MapCard from '../../MapCard';
import { EsriMapsContext } from './EsriMapsProvider';
import GeoCommIndoorMaps from './GeoCommIndoorMaps';
import TimeSliderComponent from './TimeSlider/TimeSlider';
// import IdentifyMapImageLayer from './IdentifyMapImageLayer';
// import QueryFeatureLayer from './QueryFeatureLayer';
import useDrawERA from './hooks/useDrawEra';
import useGISToken from './hooks/useGISToken';
import { W3WMarkers } from './markers/W3WMarkers';
import AllAlertMarkers from './markers/alerts/AllAlertMarkers';
import { CallMarkers } from './markers/calls/CallMarkers';
import { isIgnoreType } from './markers/calls/ignoreUtils';
import { sortLayersByDisplayPriority } from './sortLayersByDisplayPriority';
import { getMapsApiKey, updateAttributionText } from './utils';

const MapWrapper = withLayout(styled.div`
  height: 100%;
  width: 100%;
  ${({ isResizing }) => `${isResizing ? 'pointer-events: none;' : ''}`}
`);

const StyledMap = styled.div`
  width: 100%;
  height: 100%;
`;

const EsriDarkTheme = lazy(
  () => import('@rsos/maps/providers/esri/themes/DarkTheme'),
);

/**
 * @param {String} activeQueueTab - ALERTS or EMERGENCY_CALLS
 * @param {Object} currentERA - ERA data
 * @param {String} hoveredCall - Phone number of the hovered call
 * @param {Function} onMapReady - Callback function when Map is ready for JV
 * @param {Function} onMarkerAdd - Callback function when a new marker is drawn
 * @param {Function} onMarkerClick - onClick handler for markers
 * @param {Function} onMarkerHover - Hover effect handler for markers
 * @param {Function} onRemoveID - Function to set state in JV for removed alert
 * @param {Function} setMafError - Function to set medicalert error
 * @param {Function} setADRError - Function to set adr error
 * @param {String} themeName - Theme name used to switch Google maps theme
 */

/**
 * ESRI Map
 */
export const Map = ({
  activeQueueTab,
  currentERA,
  hoveredCall,
  onMapReady,
  onMarkerAdd,
  onMarkerClick,
  onMarkerHover,
  onRemoveID,
  removedID,
  setMafError,
  setADRError,
  themeName,
  triggerViewJurisdiction,
}) => {
  const { setMap: setMapForContext } = useZoomAndLocationContext();
  const isSoupMapImprovementsOn = useFlag('team-soup-map-improvements');
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  const hasBasemapSublayer = useFlag('basemap_sublayer');
  const { exact_polygon_center, shapes = [] } = useSelector(
    state => state.geofences.currentGeofence,
  );

  const { isPremiumUser } = usePortalType();

  const alerts = useSelector(state => filteredAlertsSelector(state.incidents));

  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);

  const { hoveredAlertID, hoveredFrom } = useSelector(
    state => state.hoveredIncident,
  );

  const hoveredAlert = alerts[hoveredAlertID];

  const callQueue = useSelector(state => state.emergencyCalls.callQueue);

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const hasLocation = getMostRecentLoc(callQueue[selectedCallerID]);
  const isValidCoords = isValidCoordinates(hasLocation);

  const isAllView = useMemo(() => {
    return (
      (!isValidCoords && !selectedAlertID) ||
      isIgnoreType(callQueue[selectedCallerID])
    );
  }, [isValidCoords, selectedAlertID, callQueue, selectedCallerID]);

  const hasW3W = useSelector(state => state.what3words.searched?.coordinates);

  const capabilities = useSelector(
    state => state.psaps.currentPSAP.activeCapabilities,
  );

  const gisServices = useSelector(state => state.gisServices.gisServices);

  const isMapIconDisabled = useIsMapIconDisabled();

  const hasEsriMapAccess =
    useSelector(state =>
      checkEsriMapAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && isPremiumUser;

  const hasGISServicesAccess =
    useSelector(state =>
      checkGISServicesAccess({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) && hasEsriMapAccess;

  const mapProvider = useSelector(state => state.sinatra.ui.mapProvider);

  const basemap = useSelector(state => state.sinatra.ui.basemap);

  const mapApiKey = getMapsApiKey(mapProvider);

  const loadMap = useCallback(() => {
    initializeMap({
      mapProvider,
      apiKey: mapApiKey,
      mapOptions: {
        mapContainer: mapRef.current,
        center: {
          lat: exact_polygon_center[1],
          lng: exact_polygon_center[0],
        },
        shapes: shapes,
        basemap: basemap,
      },
      viewOptions: {
        popup: {
          actions: [],
          autoOpenEnabled: false,
          dockEnabled: false,
          dockOptions: {
            buttonEnabled: false,
            breakpoint: false,
          },
          maxInlineActions: 0,
        },
      },
    }).then(map => {
      exportToGlobalNamespace('map', map);
      setMap(map);
      setMapForContext(
        map.mapInstance
          ? { mapProvider: ESRI, mapInstance: map.mapInstance }
          : null,
      );
      const geocommAttribution = capabilities[MAP_ATTRIBUTION_GEOCOMM];
      // check geocomm capability
      if (geocommAttribution && gisServices.length && hasGISServicesAccess) {
        updateAttributionText({ map, partnerText: 'GeoComm' });
      }
      // Move default zoom buttons to bottom right
      map.mapInstance.view.ui.move(['zoom'], 'bottom-right');
      onMapReady();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mapRef.current) {
      loadMap();
    }
  }, [loadMap, mapRef]);

  /*
  This effect is used to disable pointer events on the map when resizing. This fixes an issue with the
  map crashing due to a race condition relating to deck.gl layer picking events while resizing. Dynamically
  disabling pickable in the deck.gl layers still causes issues in some layers, so this is a workaround.
  Check to see if we can remove this once we upgrade to newer versions of deck.gl
  (currently using deck.gl@8.9.7)
  */
  useEffect(() => {
    const view = map?.mapInstance?.view;

    if (!view) return;
    let stationaryHandle;
    stationaryHandle = view.watch('resizing', newValue => {
      setIsResizing(newValue);
    });

    return () => {
      if (stationaryHandle) {
        stationaryHandle.remove();
      }
    };
  }, [map]);

  /**
   * Reset the map viewport back to the jurisdiction area.
   */
  useEffect(() => {
    if (!isSoupMapImprovementsOn) return;

    if (map?.mapInstance && !hasW3W) {
      map.mapInstance.view.extent = normalizeExtent(shapes);
    }
  }, [
    hasW3W,
    map?.mapInstance,
    shapes,
    triggerViewJurisdiction,
    isSoupMapImprovementsOn,
  ]);

  // Reset the map viewport back to the jurisdiction area when going from 911 event detail view
  // back to all events view.
  useEffect(() => {
    if (isSoupMapImprovementsOn) return;

    if (map?.mapInstance && isAllView && !hasW3W) {
      map.mapInstance.view.extent = normalizeExtent(shapes);
    }
  }, [
    hasW3W,
    isAllView,
    map?.mapInstance,
    shapes,
    triggerViewJurisdiction,
    isSoupMapImprovementsOn,
  ]);

  const handleCallMarkerAdd = () => {
    onMarkerAdd(EMERGENCY_CALLS);
  };

  const handleCallMarkerClick = (callerID, isAllView) => {
    onMarkerClick(callerID, EMERGENCY_CALLS, isAllView);
  };

  const handleCallMarkerHover = (callerID, secondary) => {
    onMarkerHover(callerID, 'map', EMERGENCY_CALLS, secondary);
  };

  const handleCallMarkerRemove = callerID => {
    onRemoveID(callerID);
  };

  useDrawERA(map, currentERA);
  useGISToken();
  const { cachedBasemapLayer: gisBasemapLayer, gisBasemapSublayers } =
    useGISBasemapLayer(map);
  const gisServiceLayers = useGISServiceLayers(map);

  useGISLayersOpacity(map?.mapInstance?.map);

  useEsriMapTransition(map);
  useReorderEsriMapMarkers(map);

  // A combination of GIS cached-basemap and GIS supplemental layers after it has been created.
  const combinedGISLayers = useMemo(() => {
    const layers = [];

    if (gisBasemapLayer) layers.push(gisBasemapLayer);
    /**
     * NOTE: this is not being used right now because of the new basemap sublayer feature causing labeling issues.
     * TODO: Follow up with Geocomm to see if they can fix the labeling issues on their end.
     * if (gisBasemapLayer && !hasBasemapSublayer) layers.push(gisBasemapLayer);
     * if (gisBasemapSublayers && hasBasemapSublayer) layers.push(...gisBasemapSublayers);
     */

    if (gisServiceLayers) layers.push(...gisServiceLayers);

    return layers;
  }, [
    gisBasemapLayer,
    gisServiceLayers,
    gisBasemapSublayers,
    hasBasemapSublayer,
  ]);

  // Add all of the GIS service layers to the map at once to ensure layers are added according to
  // the display_order/layerIndex property. Remove and re-add all layers to handle race conditions
  // where layers become available at different times.
  useEffect(() => {
    if (hasGISServicesAccess && map?.mapInstance && combinedGISLayers.length) {
      const sortedLayers = sortLayersByDisplayPriority(combinedGISLayers);
      // Remove all existing GIS service layers from the map to prevent ordering issues
      sortedLayers.forEach(combinedLayer => {
        const { layer } = combinedLayer;
        const existingLayer = map.mapInstance.map.findLayerById(layer.id);
        if (existingLayer) {
          map.mapInstance.map.remove(existingLayer);
        }
      });
      // Add all layers back in the correct sorted order
      sortedLayers.forEach(combinedLayer => {
        const { layer } = combinedLayer;
        map.mapInstance.map.add(layer);
      });
      // Move DeckGL layer to the top (end of layers collection) to ensure it renders above ESRI layers
      const deckGLLayer = map.mapInstance.map?.layers?.find(
        layer => layer.deck,
      );
      if (deckGLLayer) {
        const totalLayers = map.mapInstance.map.layers.length;
        map.mapInstance.map.reorder(deckGLLayer, totalLayers - 1);
      }
    }
  }, [hasGISServicesAccess, map?.mapInstance, combinedGISLayers.length]);

  return (
    <EsriMapsContext.Provider value={map}>
      <MapCard
        hasEsriMapAccess={true}
        mapRef={mapRef}
        setMafError={setMafError}
        setADRError={setADRError}
      />
      <MapWrapper isResizing={isResizing}>
        <StyledMap ref={mapRef}>
          {map?.mapInstance && (
            <>
              <W3WMarkers map={map} isMapIconDisabled={isMapIconDisabled} />
              <AllAlertMarkers
                activeQueueTab={activeQueueTab}
                hoveredAlert={hoveredAlert}
                hoveredFrom={hoveredFrom}
                map={map}
                onMarkerAdd={onMarkerAdd}
                onMarkerHover={onMarkerHover}
                onMarkerClick={onMarkerClick}
                onRemoveID={onRemoveID}
                removedID={removedID}
                isMapIconDisabled={isMapIconDisabled}
              />
              <CallMarkers
                activeQueueTab={activeQueueTab}
                hoveredCall={hoveredCall}
                hoveredFrom={hoveredFrom}
                map={map}
                onMarkerAdd={handleCallMarkerAdd}
                onMarkerClick={handleCallMarkerClick}
                onMarkerHover={handleCallMarkerHover}
                onRemoveID={handleCallMarkerRemove}
                removedID={removedID}
                isMapIconDisabled={isMapIconDisabled}
              />
              <DeckGLNext
                map={map}
                removedID={removedID}
                activeQueueTab={activeQueueTab}
                isMapIconDisabled={isMapIconDisabled}
                mapRef={mapRef}
                gisServiceLayers={gisServiceLayers}
                gisBasemapSublayers={gisBasemapSublayers}
              />
              <GeoCommIndoorMaps map={map} />
              <TimeSliderComponent map={map} />
              {/*
              NOTE: this is NOT being used anywhere.
              disabled for now because of the new click to identify feature in DeckGLNext
              {hasClickToIdentifyAccess && (
                <>
                  <IdentifyMapImageLayer
                    mapInstance={map.mapInstance}
                    ref={mapRef}
                  />
                  <QueryFeatureLayer
                    mapInstance={map.mapInstance}
                    ref={mapRef}
                  />
                </>
              )} */}
              {themeName === DARK_THEME && (
                <Suspense fallback={<></>}>
                  <EsriDarkTheme />
                </Suspense>
              )}
            </>
          )}
        </StyledMap>
        {map?.mapInstance ? (
          <RecenterMapButton mapProvider={ESRI} mapInstance={map.mapInstance} />
        ) : (
          <></>
        )}
      </MapWrapper>
    </EsriMapsContext.Provider>
  );
};
